// src/components/admin/UserLimitsDashboard.js
import React, { useState } from 'react';
import { subscriptionRequestService } from '../../services/apiServices';
import { showSuccessToast, showErrorToast, showLoadingToast, updateToast } from '../../utils/toastUtils';

const UserLimitsDashboard = ({ currentUser, onRequestSubmitted, onRefreshLimits, companyQuotas, companyUsage }) => {
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestData, setRequestData] = useState({
    requestedUsers: '',
    justification: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const limits = currentUser.userCreationLimits || {};
  const maxUsers = limits.maxSimpleUsers;
  const currentUsers = limits.currentSimpleUsers || 0;
  const remainingUsers = maxUsers !== null && maxUsers !== undefined ? maxUsers - currentUsers : null;
  const isUnlimited = maxUsers === null || maxUsers === undefined;
  const isNearLimit = !isUnlimited && remainingUsers <= 2;
  const isAtLimit = !isUnlimited && remainingUsers <= 0;

  // Company-level quotas (new source of truth, set by superadmin per enterprise)
  const hasCompanyQuotas = !!companyQuotas && (companyQuotas.maxAdmins != null || companyQuotas.maxSimpleUsers != null || companyQuotas.maxViewers != null);
  const fmtQuota = (v) => (v === null || v === undefined || v === '' ? '∞' : v);

  const handleRequestInputChange = (e) => {
    const { name, value } = e.target;
    setRequestData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRefreshLimits = async () => {
    if (!onRefreshLimits) return;
    
    setIsRefreshing(true);
    try {
      await onRefreshLimits();
      showSuccessToast('Limites mises à jour');
    } catch (error) {
      showErrorToast('Erreur lors de la mise à jour des limites');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    
    if (!requestData.requestedUsers || !requestData.justification.trim()) {
      showErrorToast('Veuillez remplir tous les champs requis');
      return;
    }

    const requestedCount = parseInt(requestData.requestedUsers);
    if (isNaN(requestedCount) || requestedCount <= 0) {
      showErrorToast('Le nombre d\'utilisateurs demandés doit être un nombre positif');
      return;
    }

    const loadingToastId = showLoadingToast('Envoi de la demande en cours...');
    setIsSubmitting(true);

    try {
      const response = await subscriptionRequestService.createRequest({
        type: 'user_limit_increase',
        requestedUsers: requestedCount,
        currentLimit: maxUsers,
        currentUsage: currentUsers,
        justification: requestData.justification.trim(),
        companyId: currentUser.companyId,
        companyName: currentUser.companyName
      });

      if (response.success) {
        updateToast(loadingToastId, 'Demande envoyée avec succès', 'success');
        setShowRequestModal(false);
        setRequestData({ requestedUsers: '', justification: '' });
        if (onRequestSubmitted) {
          onRequestSubmitted();
        }
      } else {
        throw new Error(response.message || 'Erreur lors de l\'envoi de la demande');
      }
    } catch (error) {
      console.error('Request submission error:', error);
      updateToast(loadingToastId, error.message || 'Erreur lors de l\'envoi de la demande', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusColor = () => {
    if (isUnlimited) return 'green';
    if (isAtLimit) return 'red';
    if (isNearLimit) return 'yellow';
    return 'blue';
  };

  const getStatusIcon = () => {
    if (isUnlimited) {
      return (
        <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    }
    if (isAtLimit) {
      return (
        <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      );
    }
    if (isNearLimit) {
      return (
        <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      );
    }
    return (
      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    );
  };

  const getProgressPercentage = () => {
    if (isUnlimited) return 0;
    return Math.min((currentUsers / maxUsers) * 100, 100);
  };

  const getProgressColor = () => {
    const percentage = getProgressPercentage();
    if (percentage >= 100) return 'bg-red-500';
    if (percentage >= 80) return 'bg-yellow-500';
    return 'bg-blue-500';
  };

  return (
    <>
      {hasCompanyQuotas && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-purple-800 mb-2">
            Quota entreprise
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="bg-white rounded-md p-3 border border-purple-100">
              <div className="text-gray-500">Admins</div>
              <div className="text-xl font-bold text-gray-900">
                {companyUsage?.admins ?? '-'}<span className="text-sm font-normal text-gray-500">/{fmtQuota(companyQuotas.maxAdmins)}</span>
              </div>
            </div>
            <div className="bg-white rounded-md p-3 border border-purple-100">
              <div className="text-gray-500">Analystes</div>
              <div className="text-xl font-bold text-gray-900">
                {companyUsage?.analysts ?? '-'}<span className="text-sm font-normal text-gray-500">/{fmtQuota(companyQuotas.maxSimpleUsers)}</span>
              </div>
            </div>
            <div className="bg-white rounded-md p-3 border border-purple-100">
              <div className="text-gray-500">Lecteurs</div>
              <div className="text-xl font-bold text-gray-900">
                {companyUsage?.viewers ?? '-'}<span className="text-sm font-normal text-gray-500">/{fmtQuota(companyQuotas.maxViewers)}</span>
              </div>
            </div>
          </div>
          <p className="text-xs text-purple-700 mt-3">Quota fixé par le superadmin. Création bloquée au-delà du quota (demandez une augmentation via le bouton ci-dessous).</p>
        </div>
      )}
      <div className={`bg-${getStatusColor()}-50 border border-${getStatusColor()}-200 rounded-lg p-6 mb-6`}>
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              {getStatusIcon()}
            </div>
            <div className="flex-1">
              <h3 className={`text-lg font-semibold text-${getStatusColor()}-800 mb-2`}>
                Limite de Création d'Utilisateurs
              </h3>
              
              <div className="space-y-3">
                {/* Usage Stats */}
                <div className="flex items-center space-x-4">
                  <div className={`text-2xl font-bold text-${getStatusColor()}-900`}>
                    {currentUsers}
                    {!isUnlimited && (
                      <span className={`text-lg font-normal text-${getStatusColor()}-700`}>
                        /{maxUsers}
                      </span>
                    )}
                  </div>
                  <div className={`text-sm text-${getStatusColor()}-700`}>
                    {isUnlimited ? 'Utilisateurs créés (illimité)' : 'Utilisateurs utilisés'}
                  </div>
                </div>

                {/* Progress Bar */}
                {!isUnlimited && (
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className={`h-3 rounded-full transition-all duration-300 ${getProgressColor()}`}
                      style={{ width: `${getProgressPercentage()}%` }}
                    ></div>
                  </div>
                )}

                {/* Status Message */}
                <div className={`text-sm text-${getStatusColor()}-700`}>
                  {isUnlimited && (
                    <span>✅ Vous pouvez créer un nombre illimité d'utilisateurs</span>
                  )}
                  {!isUnlimited && remainingUsers > 2 && (
                    <span>✅ {remainingUsers} utilisateur(s) restant(s)</span>
                  )}
                  {isNearLimit && !isAtLimit && (
                    <span>⚠️ Attention: Plus que {remainingUsers} utilisateur(s) restant(s)</span>
                  )}
                  {isAtLimit && (
                    <span>🚫 Limite atteinte - Vous ne pouvez plus créer d'utilisateurs</span>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex items-center space-x-3 pt-2">
                  {(isAtLimit || isNearLimit) && (
                    <button
                      onClick={() => setShowRequestModal(true)}
                      className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors text-sm font-medium"
                      disabled={isSubmitting}
                    >
                      Demander Plus d'Utilisateurs
                    </button>
                  )}
                  
                  {!isUnlimited && !isAtLimit && (
                    <button
                      onClick={() => setShowRequestModal(true)}
                      className={`bg-${getStatusColor()}-600 text-white px-3 py-1 rounded-md hover:bg-${getStatusColor()}-700 focus:outline-none focus:ring-2 focus:ring-${getStatusColor()}-500 transition-colors text-xs font-medium`}
                      disabled={isSubmitting}
                    >
                      Demander une Augmentation
                    </button>
                  )}
                  
                  {/* Refresh Button */}
                  <button
                    onClick={handleRefreshLimits}
                    className="text-gray-600 hover:text-gray-800 px-2 py-1 rounded-md hover:bg-gray-100 transition-colors text-xs font-medium flex items-center space-x-1"
                    disabled={isRefreshing || isSubmitting}
                    title="Actualiser les limites"
                  >
                    <svg 
                      className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    <span>{isRefreshing ? 'Actualisation...' : 'Actualiser'}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Request Modal */}
      {showRequestModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Demander Plus d'Utilisateurs
                </h3>
                <button
                  onClick={() => setShowRequestModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={isSubmitting}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <form onSubmit={handleRequestSubmit} className="space-y-4">
                {/* Current Status */}
                <div className="bg-gray-50 p-3 rounded-md text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Limite actuelle:</span>
                    <span className="font-medium">{isUnlimited ? 'Illimitée' : maxUsers}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Utilisateurs créés:</span>
                    <span className="font-medium">{currentUsers}</span>
                  </div>
                  {!isUnlimited && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Restants:</span>
                      <span className="font-medium">{remainingUsers}</span>
                    </div>
                  )}
                </div>

                {/* Requested Users */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre d'utilisateurs supplémentaires demandés *
                  </label>
                  <input
                    type="number"
                    name="requestedUsers"
                    value={requestData.requestedUsers}
                    onChange={handleRequestInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: 5"
                    min="1"
                    required
                    disabled={isSubmitting}
                  />
                </div>

                {/* Justification */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Justification de la demande *
                  </label>
                  <textarea
                    name="justification"
                    value={requestData.justification}
                    onChange={handleRequestInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="4"
                    placeholder="Expliquez pourquoi vous avez besoin d'utilisateurs supplémentaires..."
                    required
                    disabled={isSubmitting}
                  />
                </div>

                {/* Buttons */}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowRequestModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                    disabled={isSubmitting}
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Envoi...' : 'Envoyer la Demande'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UserLimitsDashboard;